import { useCallback, useRef } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
    activeSegmentationsMapAtom,
    autoplayEnabledAtom,
    currentPositionAtom,
    MAX_PROGRESS,
    progressAtom,
    scrubberLockAtom,
    selectedPositionAtom,
    syncedPositionAtom,
    totalFramesAtom,
    userSettingsAtom,
} from '@store';
import { Inline, LockIcon, Text, u } from '@ui';

import * as css from './PlaybackControls.css';

const DOUBLE_TAP_THRESHOLD_MS = 300;
const SWING_POSITIONS = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10'] as const;

type PlaybackPositionControlsProps = {
    syncOnly?: boolean;
    freezePosition?: string | null;
    onFreezePositionChange?: (position: string | null) => void;
};

export const PlaybackPositionControls = ({
    syncOnly,
    freezePosition,
    onFreezePositionChange,
}: PlaybackPositionControlsProps) => {
    const lastTapRef = useRef<{ position: string; time: number } | null>(null);
    const [syncedPosition, setSyncedPosition] = useAtom(syncedPositionAtom);
    const [, setAutoplayEnabled] = useAtom(autoplayEnabledAtom);
    const [scrubberLock, setScrubberLock] = useAtom(scrubberLockAtom);
    const userSettings = useAtomValue(userSettingsAtom);
    const activeSegmentationsMap = useAtomValue(activeSegmentationsMapAtom);
    const currentPosition = useAtomValue(currentPositionAtom);
    const totalFrames = useAtomValue(totalFramesAtom);
    const setProgress = useSetAtom(progressAtom);
    const setSelectedPosition = useSetAtom(selectedPositionAtom);
    const changePosition = useCallback(
        (position: string) => {
            const frame = activeSegmentationsMap?.[position];
            if (!frame) {
                console.error(`Error: unavailable segmentation frame at "${position}"`);
                return;
            }
            setProgress((frame * MAX_PROGRESS) / totalFrames);
        },
        [activeSegmentationsMap, setProgress, totalFrames],
    );
    const handlePositionClick = useCallback(
        (position: string) => {
            const now = Date.now();
            const lastTap = lastTapRef.current;
            if (lastTap && lastTap.position === position && now - lastTap.time < DOUBLE_TAP_THRESHOLD_MS) {
                onFreezePositionChange?.(freezePosition === position ? null : position);
                lastTapRef.current = null;
                return;
            }
            changePosition(position);
            lastTapRef.current = { position, time: now };
        },
        [changePosition, freezePosition, onFreezePositionChange],
    );
    const handleLockButtonClick = useCallback(() => setScrubberLock(!scrubberLock), [setScrubberLock, scrubberLock]);
    const { comparisonSwingId } = userSettings;

    return (
        <Inline gap={u(1)} className={css.controls}>
            {SWING_POSITIONS.map((pos) => {
                if (activeSegmentationsMap?.[pos] == null) return null;
                const hasComparison = Boolean(comparisonSwingId) && !syncOnly;
                return (
                    <button
                        key={pos}
                        className={css.button({
                            isActive: pos === currentPosition,
                            isFrozen: (!hasComparison && freezePosition === pos) || undefined,
                            isSynced: (hasComparison && pos === syncedPosition) || undefined,
                        })}
                        onClick={() => {
                            setAutoplayEnabled(false);
                            setSelectedPosition(pos);
                            if (hasComparison) {
                                setSyncedPosition(pos);
                                changePosition(pos);
                            } else handlePositionClick(pos);
                        }}
                    >
                        <Text variant="h3" color="inherit" as="span">
                            {pos}
                        </Text>
                    </button>
                );
            })}
            <button
                className={css.button({ isActive: Boolean(scrubberLock), isFrozen: Boolean(scrubberLock) })}
                onClick={handleLockButtonClick}
                aria-label={scrubberLock ? 'Unlock scrubber' : 'Lock scrubber to nearest position'}
            >
                <LockIcon size={20} weight={scrubberLock ? 'fill' : 'regular'} />
            </button>
        </Inline>
    );
};
